import React from 'react';

import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import CampaignRoundedIcon from '@mui/icons-material/CampaignRounded';
import TravelExploreRoundedIcon from '@mui/icons-material/TravelExploreRounded';

import type { FlowComponentRenderProps, ReferralSelectionComponentConfig } from '../../types';
import HtmlText from '../HtmlText';

const ReferralSelectionBlock: React.FC<
  FlowComponentRenderProps<ReferralSelectionComponentConfig>
> = ({ config, onAction, onInteract, setValue, value }) => {
  const selectedOptionId = typeof value === 'string' ? value : undefined;

  return (
    <Card
      sx={{
        bgcolor: 'var(--cx-brand-surface)',
        border: '1px solid',
        borderColor: 'var(--cx-brand-border)',
        boxShadow: 'none',
        color: 'var(--cx-brand-text)',
        p: 2,
      }}
    >
      <Stack spacing={1.5}>
        <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
          <CampaignRoundedIcon sx={{ color: 'var(--cx-brand-primary)', fontSize: 20 }} />
          <HtmlText
            component="div"
            html={config.title ?? 'Choose referral experience'}
            variant="subtitle2"
          />
        </Stack>

        <Stack spacing={1}>
          {config.options.map((option) => {
            const selected = selectedOptionId === option.id;

            return (
              <Button
                color="inherit"
                key={option.id}
                onClick={() => {
                  if (!onInteract) setValue(config.id, option.id);
                  (onInteract ?? onAction)?.({
                    componentId: config.id,
                    label: option.label,
                    type: 'referral',
                    value: option.id,
                  });
                }}
                sx={{
                  bgcolor: selected ? 'var(--cx-brand-button-selected-bg)' : 'var(--cx-brand-button-bg)',
                  border: '1px solid',
                  borderColor: selected
                    ? 'var(--cx-brand-button-selected-border)'
                    : 'var(--cx-brand-button-border)',
                  color: selected ? 'var(--cx-brand-button-selected-text)' : 'var(--cx-brand-button-text)',
                  justifyContent: 'flex-start',
                  minHeight: 70,
                  p: 1.25,
                  '&:hover': {
                    bgcolor: selected
                      ? 'var(--cx-brand-button-selected-bg)'
                      : 'var(--cx-brand-button-hover-bg)',
                    borderColor: selected
                      ? 'var(--cx-brand-button-selected-border)'
                      : 'var(--cx-brand-button-border)',
                  },
                }}
                type="button"
                variant="outlined"
              >
                <Box
                  sx={{
                    alignItems: 'center',
                    bgcolor: 'var(--cx-brand-button-hover-bg)',
                    borderRadius: 1,
                    color: selected ? 'var(--cx-brand-button-selected-text)' : 'var(--cx-brand-button-text)',
                    display: 'flex',
                    flexShrink: 0,
                    height: 42,
                    justifyContent: 'center',
                    mr: 1.5,
                    width: 42,
                  }}
                >
                  <TravelExploreRoundedIcon />
                </Box>

                <Box sx={{ minWidth: 0, textAlign: 'left' }}>
                  <Typography variant="subtitle2">{option.label}</Typography>
                  <HtmlText
                    component="div"
                    html={option.description}
                    variant="caption"
                    sx={{ color: 'var(--cx-brand-text-muted)' }}
                  />
                </Box>
              </Button>
            );
          })}
        </Stack>
      </Stack>
    </Card>
  );
};

export default ReferralSelectionBlock;
